import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { format, parseISO, isValid } from "date-fns";
import { toast } from "react-hot-toast";
import AppSidebar, { MobileTopBar } from "@/components/AppSidebar";
import {
  BookOpen, Plus, Trash2, PlusCircle, Eye, EyeOff, ChevronDown, ChevronUp,
  Save, Globe, GlobeLock, Pencil, ArrowLeft, Check, CalendarDays, History, Settings,
  Upload, Sparkles, Loader2, Users, X as XIcon, CalendarIcon,
} from "lucide-react";

// Guide week_start/week_end are always stored as "yyyy-MM-dd" (ISO) so the backend can sort
// guides chronologically with a plain string ORDER BY — parse/format helpers keep that guarantee.
const parseGuideDate = (str) => {
  if (!str) return undefined;
  const d = parseISO(str);
  return isValid(d) ? d : undefined;
};
const formatGuideDate = (str) => {
  const d = parseGuideDate(str);
  return d ? format(d, "MMM d, yyyy") : (str || "");
};

const DAYS_ORDER = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

// Stable client-side keys for admin-editable arrays (sermon notes, verses, events) so React
// reconciliation doesn't reuse the wrong DOM node — and its focus/state — after an add/remove.
const withKey = (item) => (item && !item._key ? { ...item, _key: crypto.randomUUID() } : item);
const withKeys = (arr) => (arr || []).map(withKey);
const withEventKeys = (events) => (events || []).map((ev) => ({ ...withKey(ev), times: withKeys(ev.times) }));
const stripKeys = (arr) => (arr || []).map(({ _key, ...rest }) => rest);

const emptyDay = (name, idx) => ({
  day_name: name,
  day_date: "",
  daily_verse_ref: "",
  daily_verse_text: "",
  has_service: name !== "Saturday",
  service_note: "",
  devotional_text: "",
  reflection_questions: [""],
  pray_over_items: [""],
  sort_order: idx,
});

const emptyGuide = () => ({
  week_label: "",
  week_start: "",
  week_end: "",
  theme: "",
  catechism_question: "",
  catechism_answer: "",
  catechism_source: "",
  sermon_scripture: "",
  sermon_title: "",
  sermon_notes: [{ text: "", answers: [""] }],
  referenced_verses: [{ reference: "", text: "" }],
  guide_events: [],
  days: DAYS_ORDER.map((n, i) => emptyDay(n, i)),
});

// ── Small helpers ────────────────────────────────────────────────────────────
const Input = ({ label, value, onChange, placeholder, className = "" }) => (
  <div className={`space-y-1 ${className}`}>
    {label && <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</label>}
    <input
      type="text"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-400 text-slate-800"
    />
  </div>
);

const Textarea = ({ label, value, onChange, placeholder, rows = 3 }) => (
  <div className="space-y-1">
    {label && <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</label>}
    <textarea
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-400 text-slate-800 resize-none"
    />
  </div>
);

// Sermon note row with blank parsing
function SermonNoteEditor({ note, idx, onChange, onRemove }) {
  const blanks = (note.text.match(/___/g) || []).length;
  const currentAnswers = note.answers || [];
  // Ensure answers array length matches blank count
  const answers = Array.from({ length: blanks }, (_, i) => currentAnswers[i] || "");

  const handleText = (val) => {
    const newBlanks = (val.match(/___/g) || []).length;
    const newAnswers = Array.from({ length: newBlanks }, (_, i) => answers[i] || "");
    onChange({ ...note, text: val, answers: newAnswers });
  };

  const handleAnswer = (i, val) => {
    const newAnswers = [...answers];
    newAnswers[i] = val;
    onChange({ ...note, answers: newAnswers });
  };

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2">
      <div className="flex items-start gap-2">
        <span className="text-xs font-bold text-slate-400 mt-2.5 shrink-0">{idx + 1}.</span>
        <div className="flex-1 space-y-2">
          <textarea
            value={note.text}
            onChange={e => handleText(e.target.value)}
            placeholder='Type sermon note… use ___ for blanks. E.g. "God is incredibly ___."'
            rows={2}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-400 resize-none"
          />
          {blanks > 0 && (
            <div className="flex flex-wrap gap-2">
              {answers.map((ans, ai) => (
                <div key={ai} className="flex items-center gap-1.5">
                  <span className="text-xs text-amber-600 font-medium">Blank {ai + 1}:</span>
                  <input
                    type="text"
                    value={ans}
                    onChange={e => handleAnswer(ai, e.target.value)}
                    placeholder="Answer"
                    className="border border-amber-200 rounded px-2 py-1 text-xs w-28 focus:outline-none focus:border-amber-500"
                  />
                </div>
              ))}
            </div>
          )}
          {blanks === 0 && note.text && (
            <p className="text-xs text-slate-400 italic">Add ___ to create fill-in-the-blank spots</p>
          )}
        </div>
        <button onClick={onRemove} className="text-slate-400 hover:text-red-500 transition-colors mt-1 shrink-0">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// Day editor section (collapsible)
function DayEditor({ day, onChange }) {
  const [open, setOpen] = useState(false);

  const updateRQ = (i, val) => {
    const qs = [...(day.reflection_questions || [])];
    qs[i] = val;
    onChange({ ...day, reflection_questions: qs });
  };
  const updatePO = (i, val) => {
    const ps = [...(day.pray_over_items || [])];
    ps[i] = val;
    onChange({ ...day, pray_over_items: ps });
  };

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center justify-between w-full px-4 py-3 bg-white hover:bg-slate-50 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-slate-700">{day.day_name}</span>
          {!day.has_service && <Badge variant="outline" className="text-xs text-slate-400">No Service</Badge>}
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>

      {open && (
        <div className="p-4 border-t border-slate-100 bg-white space-y-4">
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
              <input
                type="checkbox"
                checked={day.has_service}
                onChange={e => onChange({ ...day, has_service: e.target.checked })}
                className="rounded"
              />
              Has service / devotional content
            </label>
          </div>

          {!day.has_service ? (
            <Textarea
              label="Note (shown to users)"
              value={day.service_note || ""}
              onChange={v => onChange({ ...day, service_note: v })}
              placeholder="e.g. No Wednesday Night Service this week."
              rows={2}
            />
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input
                  label="Date"
                  value={day.day_date || ""}
                  onChange={v => onChange({ ...day, day_date: v })}
                  placeholder="e.g. Monday, May 11th, 2026"
                />
                <Input
                  label="Daily Verse Reference"
                  value={day.daily_verse_ref || ""}
                  onChange={v => onChange({ ...day, daily_verse_ref: v })}
                  placeholder="e.g. 2 Peter 3:9"
                />
              </div>
              <Textarea
                label="Daily Verse Text (optional)"
                value={day.daily_verse_text || ""}
                onChange={v => onChange({ ...day, daily_verse_text: v })}
                placeholder="Paste the verse text here…"
                rows={2}
              />
              <Textarea
                label="Devotional"
                value={day.devotional_text || ""}
                onChange={v => onChange({ ...day, devotional_text: v })}
                placeholder="Write the daily devotional…"
                rows={5}
              />

              {/* Reflection Questions */}
              <div className="space-y-2">
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Questions for Reflection
                </label>
                {(day.reflection_questions || []).map((q, i) => (
                  <div key={i} className="flex gap-2">
                    <input
                      type="text"
                      value={q}
                      onChange={e => updateRQ(i, e.target.value)}
                      placeholder={`Question ${i + 1}…`}
                      className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-400"
                    />
                    <button
                      onClick={() => onChange({ ...day, reflection_questions: (day.reflection_questions || []).filter((_, ri) => ri !== i) })}
                      className="text-slate-400 hover:text-red-500 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => onChange({ ...day, reflection_questions: [...(day.reflection_questions || []), ""] })}
                  className="text-xs text-amber-600 font-semibold hover:text-amber-700 flex items-center gap-1"
                >
                  <PlusCircle className="w-3.5 h-3.5" /> Add Question
                </button>
              </div>

              {/* Pray Over */}
              <div className="space-y-2">
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Pray Over</label>
                {(day.pray_over_items || []).map((item, i) => (
                  <div key={i} className="flex gap-2">
                    <input
                      type="text"
                      value={item}
                      onChange={e => updatePO(i, e.target.value)}
                      placeholder={`Prayer point ${i + 1}…`}
                      className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-400"
                    />
                    <button
                      onClick={() => onChange({ ...day, pray_over_items: (day.pray_over_items || []).filter((_, pi) => pi !== i) })}
                      className="text-slate-400 hover:text-red-500 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => onChange({ ...day, pray_over_items: [...(day.pray_over_items || []), ""] })}
                  className="text-xs text-amber-600 font-semibold hover:text-amber-700 flex items-center gap-1"
                >
                  <PlusCircle className="w-3.5 h-3.5" /> Add Prayer Point
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main admin page ─────────────────────────────────────────────────────────
export default function WeeklyGuideAdmin({ user }) {
  const navigate = useNavigate();
  const [guides, setGuides] = useState([]);
  const [editing, setEditing] = useState(null); // guide object being edited
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState("overview");
  const [extracting, setExtracting] = useState(false);
  const fileInputRef = useRef(null);
  const [progressGuideId, setProgressGuideId] = useState(null);
  const [progressData, setProgressData] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState(false);
  const [responsesGuideId, setResponsesGuideId] = useState(null);
  const [responsesData, setResponsesData] = useState(null);
  const [loadingResponses, setLoadingResponses] = useState(false);

  const guideNavItems = [
    { icon: CalendarDays, label: "This Week", active: false, onClick: () => navigate("/guide") },
    { icon: History, label: "Past Guides", active: false, onClick: () => navigate("/guide/archive") },
    { icon: Settings, label: "Manage Guides", active: true, onClick: () => {} },
  ];

  const loadGuides = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/guides`);
      setGuides(res.data);
    } catch {
      toast.error("Failed to load guides");
    }
  }, []);

  useEffect(() => {
    if (user && user.role !== "teacher") {
      navigate("/guide");
      return;
    }
    loadGuides();
  }, [user, navigate, loadGuides]);

  const startNew = () => {
    const g = emptyGuide();
    setEditing({ ...g, sermon_notes: withKeys(g.sermon_notes), referenced_verses: withKeys(g.referenced_verses), guide_events: withEventKeys(g.guide_events) });
  };

  const startEdit = async (guideId) => {
    try {
      const res = await axios.get(`${API}/guides/${guideId}`);
      const g = res.data;
      // Merge with emptyGuide days to ensure all days present
      const existingDays = g.days || [];
      const mergedDays = DAYS_ORDER.map((name, i) => {
        const found = existingDays.find(d => d.day_name === name);
        return found || emptyDay(name, i);
      });
      setEditing({
        ...g,
        days: mergedDays,
        sermon_notes: withKeys(g.sermon_notes),
        referenced_verses: withKeys(g.referenced_verses),
        guide_events: withEventKeys(g.guide_events),
      });
      setActiveSection("overview");
    } catch {
      toast.error("Failed to load guide");
    }
  };

  const handlePdfUpload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please select a PDF file");
      return;
    }
    setExtracting(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/guides/extract-pdf`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      const g = res.data.guide || {};
      const existingDays = g.days || [];
      const mergedDays = DAYS_ORDER.map((name, i) => {
        const found = existingDays.find((d) => d.day_name === name);
        return found ? { ...emptyDay(name, i), ...found } : emptyDay(name, i);
      });
      setEditing({
        ...emptyGuide(),
        ...g,
        days: mergedDays,
        sermon_notes: withKeys(g.sermon_notes && g.sermon_notes.length ? g.sermon_notes : emptyGuide().sermon_notes),
        referenced_verses: withKeys(g.referenced_verses && g.referenced_verses.length ? g.referenced_verses : emptyGuide().referenced_verses),
        guide_events: withEventKeys(g.guide_events),
      });
      setActiveSection("overview");
      toast.success(`Read ${res.data.pages} page(s) — review the extracted content below before saving`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not extract that PDF");
    } finally {
      setExtracting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const viewProgress = async (guideId) => {
    setProgressGuideId(guideId);
    setLoadingProgress(true);
    setProgressData(null);
    try {
      const res = await axios.get(`${API}/guides/${guideId}/progress`);
      setProgressData(res.data);
    } catch {
      toast.error("Failed to load student progress");
      setProgressGuideId(null);
    } finally {
      setLoadingProgress(false);
    }
  };

  const viewResponses = async (guideId) => {
    setResponsesGuideId(guideId);
    setLoadingResponses(true);
    setResponsesData(null);
    try {
      const res = await axios.get(`${API}/guides/${guideId}/all-responses`);
      setResponsesData(res.data);
    } catch {
      toast.error("Failed to load student responses");
      setResponsesGuideId(null);
    } finally {
      setLoadingResponses(false);
    }
  };

  const saveGuide = async () => {
    if (!editing.week_label || !editing.week_start) {
      toast.error("Week label and start date are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...editing,
        days: editing.days.filter(d => d.has_service || d.service_note),
        sermon_notes: stripKeys(editing.sermon_notes),
        referenced_verses: stripKeys(editing.referenced_verses),
        guide_events: stripKeys(editing.guide_events).map(ev => ({ ...ev, times: stripKeys(ev.times) })),
      };
      if (editing.guide_id) {
        await axios.put(`${API}/guides/${editing.guide_id}`, payload);
        toast.success("Guide updated");
      } else {
        await axios.post(`${API}/guides`, payload);
        toast.success("Guide created");
      }
      await loadGuides();
      setEditing(null);
    } catch {
      toast.error("Failed to save guide");
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (guideId) => {
    try {
      await axios.post(`${API}/guides/${guideId}/publish`);
      await loadGuides();
      toast.success("Published status updated");
    } catch {
      toast.error("Failed to update");
    }
  };

  const deleteGuide = async (guideId) => {
    if (!window.confirm("Delete this guide? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/guides/${guideId}`);
      await loadGuides();
      toast.success("Guide deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const updateDay = (dayName, updated) => {
    setEditing(prev => ({
      ...prev,
      days: prev.days.map(d => d.day_name === dayName ? updated : d),
    }));
  };

  const SECTIONS = [
    { id: "overview",  label: "Overview" },
    { id: "catechism", label: "Catechism" },
    { id: "sermon",    label: "Sermon" },
    { id: "days",      label: "Daily Guides" },
    { id: "events",    label: "Events" },
  ];

  // ── Guide list view ────────────────────────────────────────────────────────
  if (user && user.role !== "teacher") {
    return null;
  }

  if (!editing) {
    return (
      <>
      <div className="min-h-screen bg-slate-50">
      <AppSidebar user={user} active="guide" brandLabel="Weekly Guide" navItems={guideNavItems} />
      <MobileTopBar brandLabel="Weekly Guide" />
      <div className="md:ml-[68px] xl:ml-[260px] pt-14 md:pt-0">
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Weekly Guides</h1>
            <p className="text-sm text-slate-500 mt-0.5">Create and manage discipleship guides for your congregation.</p>
          </div>
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              className="hidden"
              data-testid="guide-pdf-file-input"
              onChange={(e) => handlePdfUpload(e.target.files?.[0])}
            />
            <Button
              data-testid="upload-guide-pdf-btn"
              variant="outline"
              disabled={extracting}
              onClick={() => fileInputRef.current?.click()}
              className="rounded-xl border-amber-300 text-amber-700 hover:bg-amber-50"
            >
              {extracting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Reading PDF...</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" /> Upload PDF & Auto-Fill</>
              )}
            </Button>
            <Button
              data-testid="new-guide-btn"
              onClick={startNew}
              className="bg-amber-500 hover:bg-amber-600 text-white rounded-xl"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Guide
            </Button>
          </div>
        </div>

        {guides.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-12 text-center">
            <BookOpen className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 font-medium">No guides yet</p>
            <p className="text-slate-400 text-sm mt-1">Create your first weekly guide to get started.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {guides.map(g => (
              <div key={g.guide_id} className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-slate-800 truncate">{g.week_label}</p>
                    <Badge className={g.is_published
                      ? "bg-emerald-100 text-emerald-700 border-emerald-200 text-xs"
                      : "bg-slate-100 text-slate-500 border-slate-200 text-xs"}>
                      {g.is_published ? "Published" : "Draft"}
                    </Badge>
                  </div>
                  {g.theme && <p className="text-xs text-amber-600 mt-0.5">{g.theme}</p>}
                  <p className="text-xs text-slate-400 mt-0.5">{formatGuideDate(g.week_start)} → {formatGuideDate(g.week_end)}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    data-testid={`view-progress-${g.guide_id}`}
                    onClick={() => viewProgress(g.guide_id)}
                    title="Student Progress"
                    className="p-1.5 rounded-lg text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors"
                  >
                    <Users className="w-4 h-4" />
                  </button>
                  {user?.is_super_admin && (
                    <button
                      data-testid={`view-responses-${g.guide_id}`}
                      onClick={() => viewResponses(g.guide_id)}
                      title="Student Responses (admin only)"
                      className="p-1.5 rounded-lg text-slate-400 hover:text-sky-600 hover:bg-sky-50 transition-colors"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    data-testid={`toggle-publish-${g.guide_id}`}
                    onClick={() => togglePublish(g.guide_id)}
                    title={g.is_published ? "Unpublish" : "Publish"}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                  >
                    {g.is_published ? <GlobeLock className="w-4 h-4" /> : <Globe className="w-4 h-4" />}
                  </button>
                  <button
                    data-testid={`edit-guide-${g.guide_id}`}
                    onClick={() => startEdit(g.guide_id)}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    data-testid={`delete-guide-${g.guide_id}`}
                    onClick={() => deleteGuide(g.guide_id)}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate("/guide")}
                    className="text-xs rounded-lg"
                  >
                    Preview
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      </div>
      </div>

      {/* Student Progress Dialog */}
      <Dialog open={!!progressGuideId} onOpenChange={(open) => { if (!open) { setProgressGuideId(null); setProgressData(null); } }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="guide-progress-dialog">
          <DialogHeader>
            <DialogTitle>
              {progressData ? `Student Progress — ${progressData.guide.week_label}` : "Student Progress"}
            </DialogTitle>
          </DialogHeader>
          {loadingProgress ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
            </div>
          ) : progressData ? (
            progressData.students.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-6">No students yet.</p>
            ) : (
              <div className="overflow-x-auto -mx-2">
                <table className="w-full text-sm" data-testid="guide-progress-table">
                  <thead>
                    <tr className="text-left text-xs text-slate-400 uppercase tracking-wide border-b border-slate-100">
                      <th className="py-2 px-2 font-semibold">Student</th>
                      {progressData.day_order.map(dayName => (
                        <th key={dayName} className="py-2 px-2 font-semibold text-center">{dayName.slice(0, 3)}</th>
                      ))}
                      <th className="py-2 px-2 font-semibold text-center">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {progressData.students.map(s => (
                      <tr key={s.user_id} data-testid={`progress-row-${s.user_id}`} className="border-b border-slate-50">
                        <td className="py-2 px-2 text-slate-700 font-medium">{s.name}</td>
                        {progressData.day_order.map(dayName => {
                          const status = s.per_day[dayName];
                          return (
                            <td key={dayName} className="py-2 px-2 text-center">
                              {status === "completed" ? (
                                <Check className="w-4 h-4 text-emerald-500 inline" />
                              ) : status === "not_applicable" ? (
                                <span className="text-slate-300">—</span>
                              ) : (
                                <XIcon className="w-3.5 h-3.5 text-slate-200 inline" />
                              )}
                            </td>
                          );
                        })}
                        <td className="py-2 px-2 text-center">
                          {s.all_done ? (
                            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs">
                              {s.completed}/{s.total}
                            </Badge>
                          ) : (
                            <span className="text-xs text-slate-500">{s.completed}/{s.total}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : null}
        </DialogContent>
      </Dialog>

      {/* All Student Responses Dialog (super admin only) */}
      <Dialog open={!!responsesGuideId} onOpenChange={(open) => { if (!open) { setResponsesGuideId(null); setResponsesData(null); } }}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="guide-responses-dialog">
          <DialogHeader>
            <DialogTitle>
              {responsesData ? `Student Responses — ${responsesData.guide.week_label}` : "Student Responses"}
            </DialogTitle>
          </DialogHeader>
          {loadingResponses ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
            </div>
          ) : responsesData ? (
            responsesData.days.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-6">No reflection questions on this guide yet.</p>
            ) : (
              <div className="space-y-6">
                {responsesData.days.map(day => (
                  <div key={day.day_id} data-testid={`responses-day-${day.day_id}`}>
                    <p className="text-xs font-bold uppercase tracking-wider text-amber-500 mb-3">{day.day_name}</p>
                    <div className="space-y-4">
                      {day.questions.map(q => (
                        <div key={q.index} className="space-y-2">
                          <p className="text-sm font-medium text-slate-700">
                            <span className="font-bold text-amber-500 mr-1">{q.index + 1}.</span> {q.question}
                          </p>
                          {q.answers.length === 0 ? (
                            <p className="text-xs text-slate-500 italic pl-4">No submitted responses yet.</p>
                          ) : (
                            <div className="space-y-2 pl-4">
                              {q.answers.map(a => (
                                <div key={a.user_id} data-testid={`response-${day.day_id}-${q.index}-${a.user_id}`} className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">
                                  <p className="text-xs font-semibold text-slate-600 mb-0.5">{a.name}</p>
                                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{a.text || <span className="italic text-slate-400">No response</span>}</p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : null}
        </DialogContent>
      </Dialog>
      </>
    );
  }

  // ── Edit form ──────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-50">
    <AppSidebar user={user} active="guide" brandLabel="Weekly Guide" navItems={guideNavItems} />
    <MobileTopBar brandLabel="Weekly Guide" />
    <div className="md:ml-[68px] xl:ml-[260px] pt-14 md:pt-0">
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-5">
      {/* Top bar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setEditing(null)}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-slate-900">
              {editing.guide_id ? "Edit Guide" : "New Guide"}
            </h1>
            {editing.week_label && <p className="text-xs text-slate-500">{editing.week_label}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {editing.guide_id && (
            <Button
              variant="outline"
              onClick={() => togglePublish(editing.guide_id)}
              size="sm"
              className="text-xs rounded-lg"
            >
              {guides.find(g => g.guide_id === editing.guide_id)?.is_published ? (
                <><GlobeLock className="w-3.5 h-3.5 mr-1.5" /> Unpublish</>
              ) : (
                <><Globe className="w-3.5 h-3.5 mr-1.5" /> Publish</>
              )}
            </Button>
          )}
          <Button
            data-testid="save-guide-btn"
            onClick={saveGuide}
            disabled={saving}
            className="bg-amber-500 hover:bg-amber-600 text-white rounded-xl"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving…" : "Save Guide"}
          </Button>
        </div>
      </div>

      {/* Section tabs */}
      <div className="flex gap-1 flex-wrap border-b border-slate-100 pb-2">
        {SECTIONS.map(s => (
          <button
            key={s.id}
            onClick={() => setActiveSection(s.id)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              activeSection === s.id
                ? "bg-amber-100 text-amber-800"
                : "text-slate-500 hover:text-slate-700 hover:bg-slate-100"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* ── Overview ── */}
      {activeSection === "overview" && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4">
          <Input label="Week Label *" value={editing.week_label} onChange={v => setEditing(p => ({ ...p, week_label: v }))}
            placeholder="Week of May 10th - May 17th, 2026" />
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Week Start *</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    data-testid="week-start-picker-btn"
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal border-slate-200 hover:bg-slate-50",
                      !editing.week_start && "text-slate-400"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4 text-slate-400" />
                    {editing.week_start ? formatGuideDate(editing.week_start) : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={parseGuideDate(editing.week_start)}
                    defaultMonth={parseGuideDate(editing.week_start) || new Date()}
                    onSelect={(date) => { if (date) setEditing(p => ({ ...p, week_start: format(date, "yyyy-MM-dd") })); }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Week End</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    data-testid="week-end-picker-btn"
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal border-slate-200 hover:bg-slate-50",
                      !editing.week_end && "text-slate-400"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4 text-slate-400" />
                    {editing.week_end ? formatGuideDate(editing.week_end) : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={parseGuideDate(editing.week_end)}
                    defaultMonth={parseGuideDate(editing.week_end) || parseGuideDate(editing.week_start) || new Date()}
                    onSelect={(date) => { if (date) setEditing(p => ({ ...p, week_end: format(date, "yyyy-MM-dd") })); }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <Input label="Theme" value={editing.theme || ""} onChange={v => setEditing(p => ({ ...p, theme: v }))}
            placeholder="e.g. The Light" />
        </div>
      )}

      {/* ── Catechism ── */}
      {activeSection === "catechism" && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4">
          <Textarea label="Weekly Catechism Question" value={editing.catechism_question || ""}
            onChange={v => setEditing(p => ({ ...p, catechism_question: v }))}
            placeholder="e.g. How many persons are there in the Godhead?" rows={2} />
          <Textarea label="Answer" value={editing.catechism_answer || ""}
            onChange={v => setEditing(p => ({ ...p, catechism_answer: v }))}
            placeholder="There are three persons…" rows={3} />
          <Input label="Source (optional)" value={editing.catechism_source || ""}
            onChange={v => setEditing(p => ({ ...p, catechism_source: v }))}
            placeholder="e.g. The Young Baptist's Catechism, 2007" />
        </div>
      )}

      {/* ── Sermon ── */}
      {activeSection === "sermon" && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input label="Sermon Scripture" value={editing.sermon_scripture || ""}
              onChange={v => setEditing(p => ({ ...p, sermon_scripture: v }))}
              placeholder="e.g. John 12:35-36" />
            <Input label="Sermon Title" value={editing.sermon_title || ""}
              onChange={v => setEditing(p => ({ ...p, sermon_title: v }))}
              placeholder="e.g. The Final Sermon Part 1: The Light" />
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Sermon Notes (Fill-in-the-blank)
            </label>
            <p className="text-xs text-slate-400 mt-0.5 mb-2">Use <code className="bg-slate-100 px-1 rounded">___</code> (three underscores) to mark blank spots. Enter the correct answers below each note.</p>
            <div className="space-y-2 mt-2">
              {(editing.sermon_notes || []).map((note, ni) => (
                <SermonNoteEditor
                  key={note._key ?? ni}
                  note={note}
                  idx={ni}
                  onChange={updated => setEditing(p => ({ ...p, sermon_notes: p.sermon_notes.map((n, i) => i === ni ? updated : n) }))}
                  onRemove={() => setEditing(p => ({ ...p, sermon_notes: p.sermon_notes.filter((_, i) => i !== ni) }))}
                />
              ))}
              <button
                onClick={() => setEditing(p => ({ ...p, sermon_notes: [...(p.sermon_notes || []), { text: "", answers: [], _key: crypto.randomUUID() }] }))}
                className="text-xs text-amber-600 font-semibold hover:text-amber-700 flex items-center gap-1 mt-1"
              >
                <PlusCircle className="w-3.5 h-3.5" /> Add Sermon Note
              </button>
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Referenced Bible Verses</label>
            <div className="space-y-2 mt-2">
              {(editing.referenced_verses || []).map((v, vi) => (
                <div key={v._key ?? vi} className="flex gap-2">
                  <input
                    type="text"
                    value={v.reference}
                    onChange={e => setEditing(p => ({ ...p, referenced_verses: p.referenced_verses.map((rv, i) => i === vi ? { ...rv, reference: e.target.value } : rv) }))}
                    placeholder="Reference (e.g. John 8:12)"
                    className="w-36 shrink-0 border border-slate-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-amber-400"
                  />
                  <input
                    type="text"
                    value={v.text}
                    onChange={e => setEditing(p => ({ ...p, referenced_verses: p.referenced_verses.map((rv, i) => i === vi ? { ...rv, text: e.target.value } : rv) }))}
                    placeholder="Verse text…"
                    className="flex-1 border border-slate-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-amber-400"
                  />
                  <button
                    onClick={() => setEditing(p => ({ ...p, referenced_verses: p.referenced_verses.filter((_, i) => i !== vi) }))}
                    className="text-slate-400 hover:text-red-500 shrink-0"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button
                onClick={() => setEditing(p => ({ ...p, referenced_verses: [...(p.referenced_verses || []), { reference: "", text: "", _key: crypto.randomUUID() }] }))}
                className="text-xs text-amber-600 font-semibold hover:text-amber-700 flex items-center gap-1"
              >
                <PlusCircle className="w-3.5 h-3.5" /> Add Verse
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Daily Guides ── */}
      {activeSection === "days" && (
        <div className="space-y-2">
          {(editing.days || []).map(day => (
            <DayEditor
              key={day.day_name}
              day={day}
              onChange={updated => updateDay(day.day_name, updated)}
            />
          ))}
        </div>
      )}

      {/* ── Events ── */}
      {activeSection === "events" && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-3">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Upcoming Events</label>
          {(editing.guide_events || []).map((ev, ei) => (
            <div key={ev._key ?? ei} className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={ev.date}
                  onChange={e => setEditing(p => ({ ...p, guide_events: p.guide_events.map((ge, i) => i === ei ? { ...ge, date: e.target.value } : ge) }))}
                  placeholder="Date (e.g. Sunday, May 10th)"
                  className="flex-1 border border-slate-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-amber-400"
                />
                <input
                  type="text"
                  value={ev.title}
                  onChange={e => setEditing(p => ({ ...p, guide_events: p.guide_events.map((ge, i) => i === ei ? { ...ge, title: e.target.value } : ge) }))}
                  placeholder="Event title"
                  className="flex-1 border border-slate-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-amber-400"
                />
                <button
                  onClick={() => setEditing(p => ({ ...p, guide_events: p.guide_events.filter((_, i) => i !== ei) }))}
                  className="text-slate-400 hover:text-red-500 shrink-0"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="space-y-1.5">
                {(ev.times || []).map((t, ti) => (
                  <div key={t._key ?? ti} className="flex gap-2 pl-2">
                    <input
                      type="text"
                      value={t.time}
                      onChange={e => setEditing(p => ({ ...p, guide_events: p.guide_events.map((ge, gi) => gi === ei ? { ...ge, times: ge.times.map((tm, tmi) => tmi === ti ? { ...tm, time: e.target.value } : tm) } : ge) }))}
                      placeholder="Time"
                      className="w-24 border border-slate-200 rounded px-2 py-1 text-xs focus:outline-none focus:border-amber-400"
                    />
                    <input
                      type="text"
                      value={t.description}
                      onChange={e => setEditing(p => ({ ...p, guide_events: p.guide_events.map((ge, gi) => gi === ei ? { ...ge, times: ge.times.map((tm, tmi) => tmi === ti ? { ...tm, description: e.target.value } : tm) } : ge) }))}
                      placeholder="Description"
                      className="flex-1 border border-slate-200 rounded px-2 py-1 text-xs focus:outline-none focus:border-amber-400"
                    />
                    <button
                      onClick={() => setEditing(p => ({ ...p, guide_events: p.guide_events.map((ge, gi) => gi === ei ? { ...ge, times: ge.times.filter((_, tmi) => tmi !== ti) } : ge) }))}
                      className="text-slate-300 hover:text-red-400"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => setEditing(p => ({ ...p, guide_events: p.guide_events.map((ge, gi) => gi === ei ? { ...ge, times: [...(ge.times || []), { time: "", description: "", _key: crypto.randomUUID() }] } : ge) }))}
                  className="text-xs text-amber-600 font-semibold hover:text-amber-700 flex items-center gap-1 pl-2"
                >
                  <PlusCircle className="w-3 h-3" /> Add Time
                </button>
              </div>
            </div>
          ))}
          <button
            onClick={() => setEditing(p => ({ ...p, guide_events: [...(p.guide_events || []), { date: "", title: "", times: [], _key: crypto.randomUUID() }] }))}
            className="text-xs text-amber-600 font-semibold hover:text-amber-700 flex items-center gap-1"
          >
            <PlusCircle className="w-3.5 h-3.5" /> Add Event
          </button>
        </div>
      )}

      {/* Bottom save bar */}
      <div className="flex justify-end pt-2">
        <Button
          onClick={saveGuide}
          disabled={saving}
          className="bg-amber-500 hover:bg-amber-600 text-white rounded-xl"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Saving…" : "Save Guide"}
        </Button>
      </div>
    </div>
    </div>
    </div>
  );
}
